import { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { toast } from 'sonner';
import { Plus, Edit, Trash2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Production() {
  const [workers, setWorkers] = useState([]);
  const [productions, setProductions] = useState([]);
  const [settings, setSettings] = useState(null);
  const [customMaterials, setCustomMaterials] = useState([]);
  const [editingProduction, setEditingProduction] = useState(null);
  const [deletingProduction, setDeletingProduction] = useState(null);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  
  // Form data for standard bricks
  const [formData, setFormData] = useState({
    date: new Date().toISOString().split('T')[0],
    worker_id: '',
    fly_ash: 0,
    interlock_6: 0,
    interlock_8: 0,
    cement_used: 0
  });

  // Dynamic custom material quantities
  const [customQuantities, setCustomQuantities] = useState({});
  const [editCustomQuantities, setEditCustomQuantities] = useState({});

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      const [workersRes, productionRes, settingsRes] = await Promise.all([
        axios.get(`${API}/workers`, { headers }),
        axios.get(`${API}/production`, { headers }),
        axios.get(`${API}/settings`, { headers })
      ]);
      setWorkers(workersRes.data);
      setProductions(productionRes.data);
      setSettings(settingsRes.data);
      
      // Extract custom materials from settings
      const materials = settingsRes.data.custom_materials || [];
      setCustomMaterials(materials);
      
      // Initialize custom quantities
      const initialQty = {};
      materials.forEach(m => {
        initialQty[m.id || m.name] = 0;
      });
      setCustomQuantities(initialQty);
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.worker_id) {
      toast.error('Please select a worker');
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      
      // Build other_materials array from custom quantities
      const otherMaterials = customMaterials
        .filter(m => customQuantities[m.id || m.name] > 0)
        .map(m => ({
          name: m.name,
          quantity: customQuantities[m.id || m.name],
          rate: m.rate
        }));
      
      const submitData = {
        ...formData,
        other_materials: otherMaterials
      };
      
      await axios.post(`${API}/production`, submitData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Production entry added successfully');
      
      // Reset form
      setFormData({
        date: new Date().toISOString().split('T')[0],
        worker_id: '',
        fly_ash: 0,
        interlock_6: 0,
        interlock_8: 0,
        cement_used: 0
      });
      
      // Reset custom quantities
      const resetQty = {};
      customMaterials.forEach(m => {
        resetQty[m.id || m.name] = 0;
      });
      setCustomQuantities(resetQty);
      
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add production entry');
    }
  };

  const handleEdit = (production) => {
    setEditingProduction({
      id: production.id,
      date: production.date,
      worker_id: production.worker_id,
      fly_ash: production.fly_ash,
      interlock_6: production.interlock_6,
      interlock_8: production.interlock_8,
      cement_used: production.cement_used || 0
    });
    
    // Set edit custom quantities from production's other_materials
    const editQty = {};
    customMaterials.forEach(m => {
      const found = (production.other_materials || []).find(om => om.name === m.name);
      editQty[m.id || m.name] = found ? found.quantity : 0;
    });
    setEditCustomQuantities(editQty);
    
    setShowEditDialog(true);
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      
      // Build other_materials array from edit custom quantities
      const otherMaterials = customMaterials
        .filter(m => editCustomQuantities[m.id || m.name] > 0)
        .map(m => ({
          name: m.name,
          quantity: editCustomQuantities[m.id || m.name],
          rate: m.rate
        }));
      
      const submitData = {
        date: editingProduction.date,
        worker_id: editingProduction.worker_id,
        fly_ash: editingProduction.fly_ash,
        interlock_6: editingProduction.interlock_6,
        interlock_8: editingProduction.interlock_8,
        cement_used: editingProduction.cement_used || 0,
        other_materials: otherMaterials
      };
      
      await axios.put(`${API}/production/${editingProduction.id}`, submitData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Production entry updated successfully');
      setShowEditDialog(false);
      setEditingProduction(null);
      setEditCustomQuantities({});
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update production entry');
    }
  };

  const handleDeleteConfirm = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/production/${deletingProduction.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Production entry deleted successfully');
      setShowDeleteDialog(false);
      setDeletingProduction(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete production entry');
    }
  };

  // Calculate salary for a material type
  const calculateSalary = (quantity, rate) => (quantity * rate).toFixed(2);

  // Calculate total custom materials salary
  const customMaterialsSalary = customMaterials.reduce((sum, m) => {
    return sum + (customQuantities[m.id || m.name] || 0) * (m.rate || 0);
  }, 0);

  return (
    <div data-testid="production-page" className="space-y-8">
      <div>
        <h1 className="font-manrope font-bold text-4xl text-concrete-900 tracking-tight" data-testid="production-title">Production Entry</h1>
        <p className="font-inter text-concrete-600 mt-2">Record daily brick production</p>
      </div>

      <div className="bg-white border border-concrete-200 shadow-sm rounded-md p-8">
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <Label className="font-manrope font-semibold uppercase tracking-wider text-xs text-concrete-700">Date</Label>
              <Input
                type="date"
                data-testid="production-date"
                value={formData.date}
                onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                className="mt-2 border-concrete-300 focus:border-brick-500"
                required
              />
            </div>

            <div>
              <Label className="font-manrope font-semibold uppercase tracking-wider text-xs text-concrete-700">Worker</Label>
              <Select value={formData.worker_id} onValueChange={(value) => setFormData({ ...formData, worker_id: value })}>
                <SelectTrigger data-testid="worker-select" className="mt-2 border-concrete-300 focus:border-brick-500">
                  <SelectValue placeholder="Select worker" />
                </SelectTrigger>
                <SelectContent>
                  {workers.map(worker => (
                    <SelectItem key={worker.id} value={worker.id}>{worker.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* All Brick Types */}
          <div className="border-t pt-6">
            <h3 className="font-manrope font-bold text-lg mb-4">Brick Production</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {/* Standard Bricks */}
              <div className="border border-concrete-200 rounded-lg p-4 bg-brick-50">
                <Label className="font-manrope font-semibold text-concrete-700">Fly Ash Bricks</Label>
                <p className="text-xs text-concrete-500 mb-2">Rate: ₹{settings?.fly_ash_rate?.toFixed(2) || '0.90'}/pc</p>
                <Input
                  type="number"
                  min="0"
                  data-testid="fly-ash-input"
                  value={formData.fly_ash}
                  onChange={(e) => setFormData({ ...formData, fly_ash: parseInt(e.target.value) || 0 })}
                  className="border-concrete-300 focus:border-brick-500 tabular-nums bg-white"
                  placeholder="Quantity"
                />
                {formData.fly_ash > 0 && (
                  <p className="text-right mt-2 text-sm text-brick-600 font-semibold">
                    = ₹{calculateSalary(formData.fly_ash, settings?.fly_ash_rate || 0.9)}
                  </p>
                )}
              </div>

              <div className="border border-concrete-200 rounded-lg p-4 bg-brick-50">
                <Label className="font-manrope font-semibold text-concrete-700">6" Interlock</Label>
                <p className="text-xs text-concrete-500 mb-2">Rate: ₹{settings?.interlock_6_rate?.toFixed(2) || '5.50'}/pc</p>
                <Input
                  type="number"
                  min="0"
                  data-testid="interlock-6-input"
                  value={formData.interlock_6}
                  onChange={(e) => setFormData({ ...formData, interlock_6: parseInt(e.target.value) || 0 })}
                  className="border-concrete-300 focus:border-brick-500 tabular-nums bg-white"
                  placeholder="Quantity"
                />
                {formData.interlock_6 > 0 && (
                  <p className="text-right mt-2 text-sm text-brick-600 font-semibold">
                    = ₹{calculateSalary(formData.interlock_6, settings?.interlock_6_rate || 5.5)}
                  </p>
                )}
              </div>

              <div className="border border-concrete-200 rounded-lg p-4 bg-brick-50">
                <Label className="font-manrope font-semibold text-concrete-700">8" Interlock</Label>
                <p className="text-xs text-concrete-500 mb-2">Rate: ₹{settings?.interlock_8_rate?.toFixed(2) || '6.50'}/pc</p>
                <Input
                  type="number"
                  min="0"
                  data-testid="interlock-8-input"
                  value={formData.interlock_8}
                  onChange={(e) => setFormData({ ...formData, interlock_8: parseInt(e.target.value) || 0 })}
                  className="border-concrete-300 focus:border-brick-500 tabular-nums bg-white"
                  placeholder="Quantity"
                />
                {formData.interlock_8 > 0 && (
                  <p className="text-right mt-2 text-sm text-brick-600 font-semibold">
                    = ₹{calculateSalary(formData.interlock_8, settings?.interlock_8_rate || 6.5)}
                  </p>
                )}
              </div>

              {/* Custom Materials from Settings */}
              {customMaterials.map((material) => (
                <div key={material.id || material.name} className="border border-purple-200 rounded-lg p-4 bg-purple-50">
                  <Label className="font-manrope font-semibold text-concrete-700">{material.name}</Label>
                  <p className="text-xs text-concrete-500 mb-2">Rate: ₹{(material.rate || 0).toFixed(2)}/pc</p>
                  <Input
                    type="number"
                    min="0"
                    value={customQuantities[material.id || material.name] || 0}
                    onChange={(e) => setCustomQuantities({
                      ...customQuantities,
                      [material.id || material.name]: parseInt(e.target.value) || 0
                    })}
                    className="border-concrete-300 focus:border-purple-500 tabular-nums bg-white"
                    placeholder="Quantity"
                  />
                  {(customQuantities[material.id || material.name] || 0) > 0 && (
                    <p className="text-right mt-2 text-sm text-purple-600 font-semibold">
                      = ₹{calculateSalary(customQuantities[material.id || material.name], material.rate || 0)}
                    </p>
                  )}
                </div>
              ))}
            </div>

            {customMaterials.length === 0 && (
              <p className="text-sm text-concrete-500 mt-4">
                💡 Add more brick types in <strong>Settings → Additional Brick/Material Types</strong>
              </p>
            )}
          </div>

          {/* Cement Used */}
          <div className="border-t pt-6">
            <div className="max-w-xs">
              <Label className="font-manrope font-semibold uppercase tracking-wider text-xs text-concrete-700">Cement Used (kg)</Label>
              <Input
                type="number"
                min="0"
                step="0.01"
                data-testid="cement-used-input"
                value={formData.cement_used}
                onChange={(e) => setFormData({ ...formData, cement_used: parseFloat(e.target.value) || 0 })}
                className="mt-2 border-concrete-300 focus:border-brick-500 tabular-nums"
              />
            </div>
          </div>

          <Button
            type="submit"
            data-testid="add-production-button"
            className="bg-brick-600 hover:bg-brick-500 text-white shadow-md hover:shadow-lg transition-all duration-200 font-manrope font-semibold rounded-md px-6 py-2.5"
          >
            <Plus className="mr-2 h-4 w-4" />
            Add Production Entry
          </Button>
        </form>
      </div>

      {/* Production History */}
      <div data-testid="production-list" className="bg-white border border-concrete-200 shadow-sm rounded-md overflow-hidden">
        <div className="p-6 border-b border-concrete-200">
          <h2 className="font-manrope font-bold text-xl text-concrete-900">Recent Production Entries</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-concrete-50 border-b border-concrete-200">
              <tr>
                <th className="px-4 py-3 text-left font-manrope font-semibold uppercase tracking-wider text-xs text-concrete-700">Date</th>
                <th className="px-4 py-3 text-left font-manrope font-semibold uppercase tracking-wider text-xs text-concrete-700">Worker</th>
                <th className="px-4 py-3 text-right font-manrope font-semibold uppercase tracking-wider text-xs text-concrete-700">Fly Ash</th>
                <th className="px-4 py-3 text-right font-manrope font-semibold uppercase tracking-wider text-xs text-concrete-700">6" IL</th>
                <th className="px-4 py-3 text-right font-manrope font-semibold uppercase tracking-wider text-xs text-concrete-700">8" IL</th>
                <th className="px-4 py-3 text-left font-manrope font-semibold uppercase tracking-wider text-xs text-concrete-700">Other Materials</th>
                <th className="px-4 py-3 text-right font-manrope font-semibold uppercase tracking-wider text-xs text-concrete-700">Total</th>
                <th className="px-4 py-3 text-center font-manrope font-semibold uppercase tracking-wider text-xs text-concrete-700">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-concrete-100">
              {productions.length === 0 ? (
                <tr>
                  <td colSpan="8" className="px-6 py-12 text-center font-inter text-concrete-500">No production entries yet</td>
                </tr>
              ) : (
                productions.map((prod) => {
                  const worker = workers.find(w => w.id === prod.worker_id);
                  const standardTotal = prod.fly_ash + prod.interlock_6 + prod.interlock_8;
                  const otherTotal = (prod.other_materials || []).reduce((sum, m) => sum + (m.quantity || 0), 0);
                  const total = standardTotal + otherTotal;
                  
                  return (
                    <tr key={prod.id} className="hover:bg-concrete-50">
                      <td className="px-4 py-4 font-inter text-concrete-700">{prod.date}</td>
                      <td className="px-4 py-4 font-inter text-concrete-700">{worker?.name || 'Unknown'}</td>
                      <td className="px-4 py-4 font-inter text-concrete-900 text-right tabular-nums">{prod.fly_ash}</td>
                      <td className="px-4 py-4 font-inter text-concrete-900 text-right tabular-nums">{prod.interlock_6}</td>
                      <td className="px-4 py-4 font-inter text-concrete-900 text-right tabular-nums">{prod.interlock_8}</td>
                      <td className="px-4 py-4 font-inter text-concrete-600 text-sm">
                        {prod.other_materials && prod.other_materials.length > 0 ? (
                          <div className="space-y-1">
                            {prod.other_materials.map((om, idx) => (
                              <div key={idx} className="text-xs bg-purple-100 text-purple-700 px-2 py-1 rounded inline-block mr-1">
                                {om.name}: {om.quantity}
                              </div>
                            ))}
                          </div>
                        ) : '-'}
                      </td>
                      <td className="px-4 py-4 font-inter font-semibold text-brick-600 text-right tabular-nums">{total}</td>
                      <td className="px-4 py-4">
                        <div className="flex items-center justify-center gap-2">
                          <Button
                            data-testid={`edit-production-${prod.id}`}
                            onClick={() => handleEdit(prod)}
                            size="sm"
                            variant="ghost"
                            className="text-brick-600 hover:text-brick-700 hover:bg-brick-50"
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button
                            data-testid={`delete-production-${prod.id}`}
                            onClick={() => {
                              setDeletingProduction(prod);
                              setShowDeleteDialog(true);
                            }}
                            size="sm"
                            variant="ghost"
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Edit Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-manrope font-bold">Edit Production Entry</DialogTitle>
          </DialogHeader>
          {editingProduction && (
            <form onSubmit={handleUpdate} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label className="font-manrope font-semibold uppercase tracking-wider text-xs">Date</Label>
                  <Input
                    type="date"
                    value={editingProduction.date}
                    onChange={(e) => setEditingProduction({ ...editingProduction, date: e.target.value })}
                    className="mt-2"
                    required
                  />
                </div>
                <div>
                  <Label className="font-manrope font-semibold uppercase tracking-wider text-xs">Worker</Label>
                  <select
                    value={editingProduction.worker_id}
                    onChange={(e) => setEditingProduction({ ...editingProduction, worker_id: e.target.value })}
                    className="w-full mt-2 px-3 py-2 border border-concrete-300 rounded-md focus:border-brick-500 focus:ring-brick-500/20 focus:ring-2"
                    required
                  >
                    {workers.map(w => <option key={w.id} value={w.id}>{w.name}</option>)}
                  </select>
                </div>
              </div>
              
              {/* Brick Production Edit */}
              <div className="border-t pt-4">
                <h4 className="font-semibold mb-3">Brick Production</h4>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  <div className="bg-brick-50 p-3 rounded-lg">
                    <Label className="text-xs">Fly Ash</Label>
                    <Input
                      type="number"
                      min="0"
                      value={editingProduction.fly_ash}
                      onChange={(e) => setEditingProduction({ ...editingProduction, fly_ash: parseInt(e.target.value) || 0 })}
                      className="mt-1 tabular-nums bg-white"
                    />
                  </div>
                  <div className="bg-brick-50 p-3 rounded-lg">
                    <Label className="text-xs">6" Interlock</Label>
                    <Input
                      type="number"
                      min="0"
                      value={editingProduction.interlock_6}
                      onChange={(e) => setEditingProduction({ ...editingProduction, interlock_6: parseInt(e.target.value) || 0 })}
                      className="mt-1 tabular-nums bg-white"
                    />
                  </div>
                  <div className="bg-brick-50 p-3 rounded-lg">
                    <Label className="text-xs">8" Interlock</Label>
                    <Input
                      type="number"
                      min="0"
                      value={editingProduction.interlock_8}
                      onChange={(e) => setEditingProduction({ ...editingProduction, interlock_8: parseInt(e.target.value) || 0 })}
                      className="mt-1 tabular-nums bg-white"
                    />
                  </div>
                  
                  {/* Custom Materials in Edit */}
                  {customMaterials.map((material) => (
                    <div key={material.id || material.name} className="bg-purple-50 p-3 rounded-lg">
                      <Label className="text-xs">{material.name}</Label>
                      <Input
                        type="number"
                        min="0"
                        value={editCustomQuantities[material.id || material.name] || 0}
                        onChange={(e) => setEditCustomQuantities({
                          ...editCustomQuantities,
                          [material.id || material.name]: parseInt(e.target.value) || 0
                        })}
                        className="mt-1 tabular-nums bg-white"
                      />
                    </div>
                  ))}
                </div>
              </div>

              <div className="border-t pt-4">
                <Label className="font-manrope font-semibold uppercase tracking-wider text-xs">Cement Used (kg)</Label>
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  value={editingProduction.cement_used}
                  onChange={(e) => setEditingProduction({ ...editingProduction, cement_used: parseFloat(e.target.value) || 0 })}
                  className="mt-2 tabular-nums max-w-xs"
                />
              </div>
              
              <div className="flex gap-3 pt-4">
                <Button type="submit" className="flex-1 bg-brick-600 hover:bg-brick-500">
                  Save Changes
                </Button>
                <Button type="button" variant="outline" className="flex-1" onClick={() => setShowEditDialog(false)}>
                  Cancel
                </Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="font-manrope font-bold">Delete Production Entry?</AlertDialogTitle>
            <AlertDialogDescription className="font-inter">
              This will permanently delete this production entry and update the stock accordingly. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="font-manrope">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteConfirm}
              className="bg-red-600 hover:bg-red-700 text-white font-manrope"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
